/* ============================================================
   MealPrep.nz - hero slideshow

   Crossfades hero slides. Each slide holds its own photo and its
   own stat card, so the number and the name always move with the
   picture.

   ------------------------------------------------------------
   DO NOT PAUSE ON HOVER OF THE ROOT. IT LOOKS LIKE A DEAD SLIDER.
   ------------------------------------------------------------
   The first version did `root.addEventListener('mouseenter', stop)`.
   The root is .mp-hero__art - the photo box, min(52vw, 900px)
   wide and the full height of the hero. So resting the cursor
   anywhere over the right half of the screen stopped the
   slideshow indefinitely, and nobody notices where their mouse is
   parked. LeeRoy: "the hero doesn't always change" - it was
   changing, then silently stopping whenever the pointer drifted
   over the image.

   Pause-on-hover is correct for a carousel someone is READING -
   it buys them time on a slide. This is a decorative background
   photo with no dwellable content, so the pause only ever costs
   us. It is gone.

   What is kept, and why:
     - focus pause, scoped to the DOTS only. A keyboard user
       tabbing through must not have the thing move under them.
       Scoped to the dots, not the root, so it cannot swallow the
       whole hero the way the hover did.
     - pause when the tab is hidden - no point animating offscreen.
     - no autoplay at all under prefers-reduced-motion, though the
       dots still work.
     - does nothing with a single slide.

   Interval comes from the section's "Seconds per slide" setting
   via data-interval (ms). The floor below is a guard against a
   malformed attribute, not a design choice.
   ============================================================ */
(function () {
  function init(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('.mp-hero__slide'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.mp-hero__dot'));
    if (slides.length < 2) return;
    if (root.dataset.mpHeroReady) return;
    root.dataset.mpHeroReady = '1';

    var interval = parseInt(root.getAttribute('data-interval'), 10) || 3000;
    if (interval < 1500) interval = 3000;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var i = Math.max(0, slides.findIndex(function (slide) { return slide.hasAttribute('data-active'); }));
    var timer = null;
    var pending = -1;
    var request = 0;

    function show(next) {
      next = (next + slides.length) % slides.length;
      if (next === i) { pending = -1; request++; return; }
      if (next === pending) return;
      pending = next;
      var ticket = ++request;
      var image = slides[next].querySelector('img');

      function commit() {
        if (ticket !== request || !root.isConnected) return;
        pending = -1;
        slides[i].removeAttribute('data-active');
        slides[i].setAttribute('aria-hidden', 'true');
        if (dots[i]) dots[i].setAttribute('aria-selected', 'false');
        i = next;
        slides[i].setAttribute('data-active', '');
        slides[i].removeAttribute('aria-hidden');
        if (dots[i]) dots[i].setAttribute('aria-selected', 'true');
      }

      function failed() {
        if (ticket === request) pending = -1;
      }

      if (!image) { commit(); return; }
      image.loading = 'eager';
      if (typeof image.decode === 'function') {
        image.decode().then(commit, failed);
      } else if (image.complete) {
        if (image.naturalWidth > 0) commit();
        else failed();
      } else {
        image.addEventListener('load', commit, { once: true });
        image.addEventListener('error', failed, { once: true });
      }
    }

    function start() {
      if (reduce || timer || !root.isConnected) return;
      timer = setInterval(function () {
        if (!root.isConnected) { stop(); return; }
        if (pending === -1) show(i + 1);
      }, interval);
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        show(parseInt(dot.getAttribute('data-index'), 10) || 0);
        stop();
        start();
      });

      /* keyboard only - see the note above about scope */
      dot.addEventListener('focus', stop);
      dot.addEventListener('blur', start);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });

    start();
  }

  function boot() {
    document.querySelectorAll('[data-mp-hero-slider]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* theme editor re-renders sections without a full page load */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-mp-hero-slider]').forEach(init);
  });
})();
